import { useId } from "react";

import { cn } from "@/lib/utils";

interface InitIconProps extends React.ComponentProps<"svg"> {
  flip?: boolean;
  init: boolean;
}

export function ThumbsDownInactiveIcon({
  className,
  init,
  ...props
}: InitIconProps) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "h-4 w-4.5 transition-opacity",
        !init && "opacity-30",
        className
      )}
      viewBox="0 0 18 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        className={cn(
          "transition-colors",
          init ? "fill-apricot-light" : "fill-sky-blue"
        )}
        clipRule="evenodd"
        d="M13.6 9.387v-6.77c0-.368.306-.675.677-.675h3.045a.68.68 0 01.678.675v6.77a.682.682 0 01-.678.678h-3.045a.682.682 0 01-.678-.678z"
        fillRule="evenodd"
      />
      <path
        className={cn(
          "transition-colors",
          init ? "fill-maize" : "fill-sky-blue"
        )}
        clipRule="evenodd"
        d="M3.106 11.42h2.93c-.243.974-.898 1.345-.898 2.707 0 2.484 2.83 4.603 3.543.782.213-1.028 2.554-4.751 3.595-4.833.35-.033.645-.327.645-.678v-6.78c0-.371-.317-.666-.675-.676-.372-.013-1.006-.233-1.671-.466C9.43 1.085 8.003.586 6.49.586c-1.96 0-4.183-.365-3.962 2.406-1.032.344-1.832 1.905-1.032 3.095-1.031 1.031-.847 2.057 0 3.094 0 0-.688 1.72 1.61 2.238z"
        fillRule="evenodd"
      />
    </svg>
  );
}

export function ThumbsDownActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-4 w-4.5 text-sea-blue", className)}
      viewBox="0 0 18 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M13.6 9.39V2.62c0-.367.307-.674.678-.674h3.044A.68.68 0 0118 2.62v6.768a.682.682 0 01-.678.678h-3.044a.682.682 0 01-.678-.678z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </g>
      <g filter={`url(#${id}b)`}>
        <path
          clipRule="evenodd"
          d="M3.11 11.42h2.929c-.243.975-.898 1.346-.898 2.707 0 2.484 2.829 4.602 3.543.783.212-1.028 2.553-4.75 3.594-4.832.35-.034.644-.328.644-.678V2.62c0-.37-.317-.665-.675-.675-.37-.013-1.005-.233-1.67-.466C9.433 1.09 8.006.59 6.494.59 4.532.59 2.311.225 2.531 2.996c-1.03.343-1.83 1.904-1.03 3.093-1.032 1.032-.848 2.057 0 3.094 0 0-.688 1.719 1.609 2.238z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="8.122"
          id={`${id}a`}
          width="4.4"
          x="13.6"
          y="1.945"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend in2="shape" result="effect1_innerShadow_2072_23501" />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="16.426"
          id={`${id}b`}
          width="12.128"
          x="0.794"
          y="0.574"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend in2="shape" result="effect1_innerShadow_2072_23501" />
        </filter>
      </defs>
    </svg>
  );
}

export function ThumbsUpActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-4 w-4.5 rotate-180 text-golden-brown", className)}
      viewBox="0 0 18 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M13.6 9.39V2.62c0-.367.307-.674.678-.674h3.044A.68.68 0 0118 2.62v6.768a.682.682 0 01-.678.678h-3.044a.682.682 0 01-.678-.678z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </g>
      <g filter={`url(#${id}b)`}>
        <path
          clipRule="evenodd"
          d="M3.11 11.42h2.929c-.243.975-.898 1.346-.898 2.707 0 2.484 2.829 4.602 3.543.783.212-1.028 2.553-4.75 3.594-4.832.35-.034.644-.328.644-.678V2.62c0-.37-.317-.665-.675-.675-.37-.013-1.005-.233-1.67-.466C9.433 1.09 8.006.59 6.494.59 4.532.59 2.311.225 2.531 2.996c-1.03.343-1.83 1.904-1.03 3.093-1.032 1.032-.848 2.057 0 3.094 0 0-.688 1.719 1.609 2.238z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="8.122"
          id={`${id}a`}
          width="4.4"
          x="13.6"
          y="1.945"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend in2="shape" result="effect1_innerShadow_2072_23501" />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="16.426"
          id={`${id}b`}
          width="12.128"
          x="0.794"
          y="0.574"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend in2="shape" result="effect1_innerShadow_2072_23501" />
        </filter>
      </defs>
    </svg>
  );
}

export function ThumbsUpInactiveIcon({
  className,
  init,
  ...props
}: InitIconProps) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "h-4 w-4.5 rotate-180 transition-opacity",
        !init && "opacity-30",
        className
      )}
      viewBox="0 0 18 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        className={cn(
          "transition-colors",
          init ? "fill-apricot-light" : "fill-golden-brown"
        )}
        clipRule="evenodd"
        d="M13.6 9.387v-6.77c0-.368.306-.675.677-.675h3.045a.68.68 0 01.678.675v6.77a.682.682 0 01-.678.678h-3.045a.682.682 0 01-.678-.678z"
        fillRule="evenodd"
      />
      <path
        className={cn(
          "transition-colors",
          init ? "fill-maize" : "fill-golden-brown"
        )}
        clipRule="evenodd"
        d="M3.106 11.42h2.93c-.243.974-.898 1.345-.898 2.707 0 2.484 2.83 4.603 3.543.782.213-1.028 2.554-4.751 3.595-4.833.35-.033.645-.327.645-.678v-6.78c0-.371-.317-.666-.675-.676-.372-.013-1.006-.233-1.671-.466C9.43 1.085 8.003.586 6.49.586c-1.96 0-4.183-.365-3.962 2.406-1.032.344-1.832 1.905-1.032 3.095-1.031 1.031-.847 2.057 0 3.094 0 0-.688 1.72 1.61 2.238z"
        fillRule="evenodd"
      />
    </svg>
  );
}

export function HeartIconInactive({
  className,
  init,
  ...props
}: InitIconProps) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "h-[15px] w-4 animate-in transition-opacity",
        !init && "opacity-30",
        className
      )}
      viewBox="0 0 16 15"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        className="fill-maize"
        d="M7.695 14.321c.301 0 .76-.28 1.205-.58 3.872-2.575 6.49-5.673 6.49-8.771 0-3.005-2.116-4.97-4.525-4.97-1.506 0-2.553.825-3.17 1.986C7.085.833 6.031 0 4.525 0 2.115 0 0 1.965 0 4.97c0 3.098 2.618 6.196 6.49 8.77.445.302.904.581 1.205.581z"
      />
      <path
        className="fill-apricot-light"
        d="M4.525 0c1.506 0 2.56.832 3.17 1.986 0 0-9.294.63-1.205 11.755C2.618 11.166 0 8.068 0 4.97 0 1.965 2.116 0 4.525 0z"
      />
    </svg>
  );
}

export function HeartIconActive({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-[15px] w-4 animate-in text-chocolate", className)}
      viewBox="0 0 16 15"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id})`}>
        <path
          d="M7.695 14.321c.301 0 .76-.28 1.205-.58 3.872-2.575 6.49-5.673 6.49-8.771 0-3.005-2.116-4.97-4.525-4.97-1.506 0-2.553.825-3.17 1.986C7.085.833 6.031 0 4.525 0 2.115 0 0 1.965 0 4.97c0 3.098 2.618 6.196 6.49 8.77.445.302.904.581 1.205.581z"
          fill="currentColor"
          fillOpacity="0.5"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="14.321"
          id={id}
          width="15.39"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" />
          <feBlend in2="shape" result="effect1_innerShadow_2072_23320" />
        </filter>
      </defs>
    </svg>
  );
}
